import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { bearingDeg, destinationPoint, normalizeDegrees } from '../../game/geo';
import type { NeighborLink, Panorama, PanoramaProvider } from '../../providers/types';
import { IconArrowRight, IconMinus, IconPlus, IconUndo } from '../Icons';
import Compass from './Compass';
import type { PanoramaControls } from './controls';
import type { GoogleStreetViewHandle } from './GoogleStreetView';

/** Pitch (degrees) at which the navigation arrows are drawn on the road. */
const ARROW_PITCH = -32;
/** Approximate height of street-level cameras, used to turn a click on the ground into a distance. */
const CAMERA_HEIGHT_M = 2.5;
const MIN_JUMP_M = 4;
const MAX_JUMP_M = 70;

const GoogleStreetView = lazy(() => import('./GoogleStreetView'));
const SphereViewer = lazy(() => import('./SphereViewer'));

interface PanoramaViewProps {
  /** Starting panorama of the round (the location to guess). */
  panorama: Panorama;
  provider: PanoramaProvider;
  allowMove: boolean;
  onReady: () => void;
  onFailed: () => void;
}

export default function PanoramaView({ panorama, provider, allowMove, onReady, onFailed }: PanoramaViewProps) {
  const [current, setCurrent] = useState<Panorama>(panorama);
  const [keepHeading, setKeepHeading] = useState<number | null>(null);
  const [heading, setHeading] = useState(panorama.heading);
  const [ready, setReady] = useState(false);
  const [neighbors, setNeighbors] = useState<NeighborLink[]>([]);
  const [movingTo, setMovingTo] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [googleMoved, setGoogleMoved] = useState(false);
  const [, setViewVersion] = useState(0);
  const viewFrame = useRef(0);
  const onViewChange = useCallback(() => {
    cancelAnimationFrame(viewFrame.current);
    viewFrame.current = requestAnimationFrame(() => setViewVersion((v) => v + 1));
  }, []);
  useEffect(() => () => cancelAnimationFrame(viewFrame.current), []);
  const previousRef = useRef<Panorama>(panorama);
  const readyRef = useRef(false);
  const sphereRef = useRef<PanoramaControls>(null);
  const googleRef = useRef<GoogleStreetViewHandle>(null);
  const neighborCache = useRef(new Map<string, Promise<Panorama>>());
  const headingRef = useRef(heading);
  headingRef.current = heading;
  const isGoogle = provider.viewer === 'google';

  // New round: back to the starting panorama.
  useEffect(() => {
    setCurrent(panorama);
    previousRef.current = panorama;
    setKeepHeading(null);
    readyRef.current = false;
    setReady(false);
    setMessage(null);
    setGoogleMoved(false);
  }, [panorama]);

  /** Loads a neighbour once; the promise is shared between prefetch and click. */
  const loadNeighbor = useCallback((link: NeighborLink) => {
    let promise = neighborCache.current.get(link.id);
    if (!promise) {
      promise = link.load();
      promise.catch(() => neighborCache.current.delete(link.id));
      neighborCache.current.set(link.id, promise);
    }
    return promise;
  }, []);

  // Neighbouring pictures, to move along the road. They are prefetched so moving is instant.
  useEffect(() => {
    setNeighbors([]);
    if (!allowMove || isGoogle || !provider.getNeighbors) return;
    const controller = new AbortController();
    provider
      .getNeighbors(current, controller.signal)
      .then((links) => {
        if (controller.signal.aborted) return;
        setNeighbors(links);
        for (const link of links) {
          loadNeighbor(link)
            .then((next) => sphereRef.current?.preload?.(next))
            .catch(() => undefined);
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [current, allowMove, isGoogle, provider, loadNeighbor]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 3500);
    return () => window.clearTimeout(timer);
  }, [message]);

  const handleLoaded = useCallback(() => {
    setMovingTo(null);
    if (readyRef.current) return;
    readyRef.current = true;
    setReady(true);
    onReady();
  }, [onReady]);

  const handleError = useCallback(() => {
    if (current === panorama) {
      onFailed();
      return;
    }
    // A neighbour failed to load: go back to where we were.
    setMovingTo(null);
    setMessage('Impossible d’avancer dans cette direction.');
    setKeepHeading(headingRef.current);
    setCurrent(previousRef.current);
  }, [current, panorama, onFailed]);

  const moveTo = useCallback(
    async (link: NeighborLink) => {
      if (movingTo) return;
      setMovingTo(link.id);
      try {
        const next = await loadNeighbor(link);
        previousRef.current = current;
        setKeepHeading(headingRef.current);
        setCurrent(next);
      } catch {
        setMovingTo(null);
        setMessage('Impossible d’avancer dans cette direction.');
      }
    },
    [current, movingTo, loadNeighbor],
  );

  /** Click-to-go: a click on the ground moves to the picture closest to that spot. */
  const [clickMarker, setClickMarker] = useState<{ x: number; y: number } | null>(null);
  const goToClicked = useCallback(
    async ({ heading: clickHeading, pitch, x, y }: { heading: number; pitch: number; x: number; y: number }) => {
      if (!allowMove || !provider.findNearby || movingTo) return;
      if (pitch > -1.5) {
        setMessage('Cliquez sur la route ou le sol pour vous y déplacer.');
        return;
      }
      const distanceM = Math.min(MAX_JUMP_M, Math.max(MIN_JUMP_M, CAMERA_HEIGHT_M / Math.tan((-pitch * Math.PI) / 180)));
      const target = destinationPoint(current.position, clickHeading, distanceM / 1000);
      const radiusKm = Math.max(12, distanceM * 0.4) / 1000;
      setMovingTo('click');
      setClickMarker({ x, y });
      try {
        const next = await provider.findNearby(target, { radiusKm, excludeId: current.id });
        if (!next) {
          setMovingTo(null);
          setClickMarker(null);
          setMessage('Aucune photo par ici.');
          return;
        }
        previousRef.current = current;
        setKeepHeading(headingRef.current);
        setCurrent(next);
      } catch {
        setMovingTo(null);
        setClickMarker(null);
        setMessage('Impossible de se déplacer ici.');
      }
    },
    [allowMove, provider, movingTo, current],
  );

  useEffect(() => {
    if (movingTo === null) setClickMarker(null);
  }, [movingTo]);

  const returnToStart = () => {
    if (isGoogle) {
      googleRef.current?.returnToStart();
      return;
    }
    previousRef.current = panorama;
    setKeepHeading(null);
    setCurrent(panorama);
  };

  const controls = (): PanoramaControls | null => (isGoogle ? googleRef.current : sphereRef.current);
  const moved = isGoogle ? googleMoved : current !== panorama;

  return (
    <div className="panorama-view">
      {isGoogle ? (
        <Suspense fallback={null}>
          <GoogleStreetView
            ref={googleRef}
            panorama={panorama}
            allowMove={allowMove}
            onLoaded={handleLoaded}
            onError={handleError}
            onHeadingChange={setHeading}
            onMovedChange={setGoogleMoved}
          />
        </Suspense>
      ) : (
        <Suspense fallback={null}>
          <SphereViewer
            ref={sphereRef}
            panorama={current}
            keepHeading={keepHeading}
            onLoaded={handleLoaded}
            onError={handleError}
            onHeadingChange={setHeading}
            onViewChange={onViewChange}
            onClick={goToClicked}
          />
        </Suspense>
      )}

      {!ready && (
        <div className="panorama-view__loading" role="status">
          <span className="spinner" aria-hidden="true" />
          Chargement du panorama…
        </div>
      )}

      {ready && neighbors.length > 0 && (
        <div className="nav-arrows" aria-label="Se déplacer">
          {neighbors.map((link) => {
            const bearing = bearingDeg(current.position, link.position);
            const relative = Math.round(normalizeDegrees(bearing - heading));
            // Anchored on the ground towards the neighbour, like Street View: it follows rotation, tilt and zoom.
            const near = sphereRef.current?.project?.(bearing, ARROW_PITCH);
            const far = sphereRef.current?.project?.(bearing, ARROW_PITCH + 8);
            if (!near || !far) return null;
            const angle = (Math.atan2(far.y - near.y, far.x - near.x) * 180) / Math.PI;
            return (
              <button
                key={link.id}
                type="button"
                className={`nav-arrow ${movingTo === link.id ? 'nav-arrow--busy' : ''}`}
                style={{ left: near.x, top: near.y, transform: `translate(-50%, -50%) rotate(${angle}deg)` }}
                onClick={() => moveTo(link)}
                disabled={movingTo !== null}
                title="Avancer dans cette direction"
                aria-label={`Avancer (direction ${relative}° par rapport à la vue)`}
              >
                <IconArrowRight size={22} />
              </button>
            );
          })}
        </div>
      )}

      {clickMarker && <span className="click-marker" style={{ left: clickMarker.x, top: clickMarker.y }} aria-hidden="true" />}

      <div className="panorama-controls">
        <Compass heading={heading} onFaceNorth={() => controls()?.faceHeading(0)} />
        <div className="panorama-controls__group">
          <button type="button" className="icon-btn" onClick={() => controls()?.zoomIn()} title="Zoomer" aria-label="Zoomer">
            <IconPlus />
          </button>
          <button type="button" className="icon-btn" onClick={() => controls()?.zoomOut()} title="Dézoomer" aria-label="Dézoomer">
            <IconMinus />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={returnToStart}
            disabled={!moved && !isGoogle}
            title="Revenir au point de départ"
            aria-label="Revenir au point de départ"
          >
            <IconUndo />
          </button>
        </div>
      </div>

      {message && (
        <div className="toast toast--small" role="status">
          {message}
        </div>
      )}
    </div>
  );
}
